/* ThePulseStrip — compact live activity strip.
 * Renders 4-6 latest events in a horizontal scroller with a colored dot
 * per tone, event icon, headline, timestamp, and a "See Live Feed →"
 * pill on the right. Auto-refreshes every 30s.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, ArrowRight, ShieldCheck, BadgeCheck, Sparkles, Newspaper, Film, Video,
  Pencil, Link as LinkIcon, Flag, Trophy, Award, Handshake, Radio, Rocket,
  Megaphone, Flame, UserPlus, ListChecks, ArrowRightLeft, Users,
} from 'lucide-react';
import VerifiedCheck from '../VerifiedCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  'shield-check': ShieldCheck,
  'badge-check':  BadgeCheck,
  'sparkles':     Sparkles,
  'newspaper':    Newspaper,
  'film':         Film,
  'video':        Video,
  'pencil':       Pencil,
  'link':         LinkIcon,
  'flag':         Flag,
  'arrow-right-left': ArrowRightLeft,
  'trophy':       Trophy,
  'award':        Award,
  'handshake':    Handshake,
  'radio':        Radio,
  'users-plus':   UserPlus,
  'user-plus':    UserPlus,
  'list':         ListChecks,
  'rocket':       Rocket,
  'megaphone':    Megaphone,
  'flame':        Flame,
  'activity':     Activity,
  'users':        Users,
};

const TONE_COLOR = {
  blue:    { dot: '#60a5fa', text: 'text-blue-300' },
  emerald: { dot: '#34d399', text: 'text-emerald-300' },
  green:   { dot: '#4ade80', text: 'text-green-300' },
  amber:   { dot: '#fbbf24', text: 'text-amber-300' },
  slate:   { dot: '#94a3b8', text: 'text-slate-300' },
  purple:  { dot: '#a78bfa', text: 'text-purple-300' },
  orange:  { dot: '#fb923c', text: 'text-orange-300' },
  cyan:    { dot: '#22d3ee', text: 'text-cyan-300' },
  rose:    { dot: '#fb7185', text: 'text-rose-300' },
};

/** Compact relative-time formatter — "2m", "8m", "3h", "2d". */
export const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
};

/** Choose the right destination for each event's CTA. */
export const eventHref = (ev) => {
  // iter264 · Admin override wins. Full URLs are collapsed to a site
  // path so <Link> navigation keeps working.
  if (ev.link_url) {
    const l = String(ev.link_url).trim();
    if (/^https?:\/\//i.test(l)) {
      try {
        const u = new URL(l);
        return `${u.pathname}${u.search}${u.hash}` || '/pulse';
      } catch { /* fall through */ }
    }
    return l.startsWith('/') ? l : `/${l}`;
  }
  if (ev.target_type === 'story' && ev.target_slug) return `/stories/${ev.target_slug}`;
  if (ev.target_type === 'athlete' && ev.target_slug) return `/network/${ev.target_slug}`;
  if (ev.target_type === 'program' && ev.target_slug) return `/school-network/${ev.target_slug}`;
  if (ev.actor_type === 'athlete' && ev.actor_slug) return `/network/${ev.actor_slug}`;
  if (ev.actor_type === 'program' && ev.actor_slug) return `/school-network/${ev.actor_slug}`;
  return '/pulse';
};

const ThePulseStrip = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/pulse/live?limit=6`, { withCredentials: true });
        if (!cancel) setEvents(data?.events || []);
      } catch { /* silent — strip just hides */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const items = useMemo(() => events.slice(0, 6), [events]);
  if (items.length === 0) return null;

  return (
    <section
      className="rounded-2xl bg-black ring-1 ring-white/10 px-4 py-3 sm:px-5 sm:py-4"
      data-testid="pulse-strip"
      aria-label="The Pulse — live activity"
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
            <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-400" />
          </span>
          <p className="font-display text-xs sm:text-sm uppercase tracking-[0.28em] text-white font-bold">
            The Pulse
          </p>
          <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">· Live</span>
        </div>
        <Link
          to="/pulse"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white text-[10px] font-bold uppercase tracking-widest ring-1 ring-white/10 transition"
          data-testid="pulse-strip-see-feed"
        >
          See live feed <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <ul className="flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
        {items.map((ev) => {
          const Icon = ICON_MAP[ev.icon] || Activity;
          const tone = TONE_COLOR[ev.tone] || TONE_COLOR.slate;
          return (
            <li key={ev.id} className="flex-shrink-0">
              <Link
                to={eventHref(ev)}
                className="group flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/[0.03] ring-1 ring-white/10 hover:bg-white/[0.08] hover:ring-white/25 transition max-w-[320px]"
                data-testid={`pulse-strip-event-${ev.id}`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tone.dot }} />
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${tone.text}`} />
                <div className="min-w-0 flex items-center gap-1.5">
                  <span className="text-[12px] text-white font-medium truncate max-w-[220px]">
                    {ev.headline}
                  </span>
                  {ev.verified && <VerifiedCheck size={12} />}
                  <span className="text-[11px] text-white/40 font-mono flex-shrink-0">· {timeAgo(ev.created_at)}</span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ThePulseStrip;
